/**
 * The Solana note namespace.
 *
 * A Solana note is a flat V2 note whose namespace fields name the settlement
 * network, the circuit chain domain and the pool program instead of an EVM
 * chain id and contract. EVM code filters on `chainId`, so these notes are
 * invisible to it, and this module refuses EVM-shaped records in turn.
 */
import {
  SOLANA_TOKEN_2022_PROGRAM_ID,
  SOLANA_TOKEN_PROGRAM_ID,
  solanaPubkeyBase58,
  solanaPubkeyBytes,
} from "@stealthos/sdk";
import { ethers } from "ethers";
import { computeCommitment, computeNPK } from "../../joinsplit/keys";
import { NoteTokenType, type JoinSplitNote } from "../../joinsplit/types";
import { BN254_SCALAR_FIELD } from "../notes";
import type { SolanaNetworkConfig } from "../../config/solana";
import { fail } from "./common";

const SOLANA_NOTE_PROTOCOL = "flat-joinsplit-v2" as const;
/** `tokenData.tokenAddress` of a native SOL note. */
const SOLANA_NATIVE_TOKEN_ADDRESS = `0x${"00".repeat(32)}`;

export type SolanaNoteNetwork = Pick<
  SolanaNetworkConfig,
  "key" | "programId" | "chainDomainId"
>;

function solanaChainDomainWord(domain: bigint): `0x${string}` {
  return `0x${domain.toString(16).padStart(64, "0")}`;
}

export function solanaTreeKey(
  networkKey: string,
  programId: string,
  treeIndex: number | bigint,
): string {
  return `${SOLANA_NOTE_PROTOCOL}:${networkKey}:${solanaPubkeyBase58(programId, "programId")}:${BigInt(treeIndex)}`;
}

/** Mint as the note's 32-byte token address; zero for native SOL. */
export function solanaNoteTokenAddress(mint: string | null): string {
  if (mint === null) return SOLANA_NATIVE_TOKEN_ADDRESS;
  return ethers.hexlify(solanaPubkeyBytes(mint, "mint"));
}

export function solanaMintFromNoteTokenAddress(
  tokenAddress: string,
): string | null {
  if (!ethers.isHexString(tokenAddress, 32)) {
    fail("note token address must be a 32-byte hex word");
  }
  if (tokenAddress.toLowerCase() === SOLANA_NATIVE_TOKEN_ADDRESS) return null;
  return solanaPubkeyBase58(ethers.getBytes(tokenAddress), "mint");
}

function tokenTypeFor(tokenProgram: string): NoteTokenType {
  if (tokenProgram === SOLANA_TOKEN_PROGRAM_ID) return NoteTokenType.SOL_SPL;
  if (tokenProgram === SOLANA_TOKEN_2022_PROGRAM_ID) {
    return NoteTokenType.SOL_TOKEN2022;
  }
  return fail("token program must be SPL Token or Token-2022");
}

export function solanaTokenProgramForNote(note: JoinSplitNote): string {
  return note.tokenData?.tokenType === NoteTokenType.SOL_TOKEN2022
    ? SOLANA_TOKEN_2022_PROGRAM_ID
    : SOLANA_TOKEN_PROGRAM_ID;
}

function parseTokenHash(value: string | bigint): bigint {
  const parsed = typeof value === "bigint" ? value : BigInt(value);
  if (parsed <= 0n || parsed >= BN254_SCALAR_FIELD) {
    fail("token hash must be a nonzero field element");
  }
  return parsed;
}

function randomFieldElement(): bigint {
  for (let attempt = 0; attempt < 128; attempt += 1) {
    const candidate =
      BigInt(ethers.hexlify(ethers.randomBytes(32))) % BN254_SCALAR_FIELD;
    if (candidate !== 0n) return candidate;
  }
  return fail("secure randomness did not produce a field element");
}

export async function createSolanaFlatNote(params: {
  recipientMpk: bigint;
  network: SolanaNoteNetwork;
  treeIndex: number | bigint;
  mint: string | null;
  tokenProgram?: string;
  tokenHash: string | bigint;
  value: bigint;
  random?: bigint;
}): Promise<JoinSplitNote> {
  if (params.recipientMpk <= 0n || params.recipientMpk >= BN254_SCALAR_FIELD) {
    fail("recipient MPK is outside the BN254 scalar field");
  }
  if (params.value <= 0n || params.value >= 1n << 120n) {
    fail("note value must fit the nonzero uint120 range");
  }
  const treeIndex = Number(params.treeIndex);
  if (!Number.isSafeInteger(treeIndex) || treeIndex < 0) {
    fail("invalid note tree index");
  }
  const programId = solanaPubkeyBase58(params.network.programId, "programId");
  const mint =
    params.mint === null ? null : solanaPubkeyBase58(params.mint, "mint");
  const tokenProgram = params.tokenProgram ?? SOLANA_TOKEN_PROGRAM_ID;
  const tokenHash = parseTokenHash(params.tokenHash);
  const random = params.random ?? randomFieldElement();
  if (random <= 0n || random >= BN254_SCALAR_FIELD) {
    fail("note blinding factor is outside the BN254 scalar field");
  }
  const npk = await computeNPK(params.recipientMpk, random);
  const commitment = await computeCommitment(npk, tokenHash, params.value);
  return {
    schemaVersion: 2,
    protocol: SOLANA_NOTE_PROTOCOL,
    network: params.network.key,
    chainDomainId: solanaChainDomainWord(params.network.chainDomainId),
    poolAddress: programId,
    npk,
    random,
    commitment,
    tokenHash,
    tokenData: {
      tokenAddress: solanaNoteTokenAddress(mint),
      tokenType: tokenTypeFor(tokenProgram),
      tokenSubID: "0",
      ...(mint ? { tokenMint: mint } : {}),
    },
    value: params.value,
    leafIndex: -1,
    treeIndex,
    treeKey: solanaTreeKey(params.network.key, programId, treeIndex),
  };
}

/** Older inbox envelopes omitted the network fields but retained a scoped tree
 * key. Restore only a matching namespace; ownership, commitment and chain
 * inclusion must still be authenticated by synchronizeSolanaNotes. */
export function restoreSolanaInboxNamespace(
  note: JoinSplitNote,
  network: SolanaNoteNetwork,
): JoinSplitNote {
  if (
    note.network !== undefined ||
    note.chainDomainId !== undefined ||
    note.chainId !== undefined ||
    note.schemaVersion !== 2 ||
    note.protocol !== SOLANA_NOTE_PROTOCOL ||
    note.poolAddress !== network.programId ||
    !Number.isSafeInteger(note.treeIndex) ||
    note.treeIndex! < 0 ||
    note.treeKey !==
      solanaTreeKey(network.key, network.programId, note.treeIndex!)
  )
    return note;
  return {
    ...note,
    network: network.key,
    chainDomainId: solanaChainDomainWord(network.chainDomainId),
  };
}

/** Cheap namespace check used to pick candidates before any authentication. */
export function isSolanaFlatNote(
  note: JoinSplitNote | undefined,
  network: SolanaNoteNetwork,
): boolean {
  return Boolean(
    note &&
    note.schemaVersion === 2 &&
    note.protocol === SOLANA_NOTE_PROTOCOL &&
    note.network === network.key &&
    note.chainId === undefined &&
    typeof note.chainDomainId === "string" &&
    note.chainDomainId.toLowerCase() ===
      solanaChainDomainWord(network.chainDomainId) &&
    note.poolAddress === network.programId &&
    note.tokenData !== undefined &&
    (note.tokenData.tokenType === NoteTokenType.SOL_SPL ||
      note.tokenData.tokenType === NoteTokenType.SOL_TOKEN2022),
  );
}

/** The note's mint, or null for native SOL; throws on a malformed record. */
export function solanaNoteMint(note: JoinSplitNote): string | null {
  if (!note.tokenData) fail("note has no token identity");
  const mint = solanaMintFromNoteTokenAddress(note.tokenData.tokenAddress);
  if (
    note.tokenData.tokenMint !== undefined &&
    note.tokenData.tokenMint !== mint
  ) {
    fail("note token mint does not match its token address");
  }
  return mint;
}

/**
 * Authenticate every encrypted-note field before the record is considered
 * spendable. Merkle membership and nullifier state are checked separately.
 */
export async function assertSolanaFlatNote(params: {
  note: JoinSplitNote;
  network: SolanaNoteNetwork;
  /** `undefined` skips the token identity check; `null` means native SOL. */
  mint?: string | null;
  expectedTokenHash?: string | bigint;
  expectedMpk?: bigint;
}): Promise<void> {
  const { note, network } = params;
  if (!isSolanaFlatNote(note, network)) {
    fail("encrypted note belongs to a different protocol namespace");
  }
  if (
    note.treeIndex === undefined ||
    !Number.isSafeInteger(note.treeIndex) ||
    note.treeIndex < 0 ||
    note.treeKey !==
      solanaTreeKey(network.key, network.programId, note.treeIndex)
  ) {
    fail("encrypted note has invalid tree metadata");
  }
  if (note.value <= 0n || note.value >= 1n << 120n) {
    fail("encrypted note has an invalid value");
  }
  const tokenHash = parseTokenHash(note.tokenHash);
  if (
    params.expectedTokenHash !== undefined &&
    tokenHash !== parseTokenHash(params.expectedTokenHash)
  ) {
    fail("encrypted note token domain does not match the pool");
  }
  if (BigInt(note.tokenData!.tokenSubID || "0") !== 0n) {
    fail("encrypted note token identity does not match the action");
  }
  const noteMint = solanaNoteMint(note);
  if (params.mint !== undefined) {
    const expectedMint =
      params.mint === null ? null : solanaPubkeyBase58(params.mint, "mint");
    if (noteMint !== expectedMint) {
      fail("encrypted note token identity does not match the action");
    }
  }
  if (
    note.npk <= 0n ||
    note.npk >= BN254_SCALAR_FIELD ||
    note.random <= 0n ||
    note.random >= BN254_SCALAR_FIELD
  ) {
    fail("encrypted note contains a non-canonical field element");
  }
  if (params.expectedMpk !== undefined) {
    const expectedNpk = await computeNPK(params.expectedMpk, note.random);
    if (expectedNpk !== note.npk) {
      fail("encrypted note is not owned by the connected privacy key");
    }
  }
  const expectedCommitment = await computeCommitment(
    note.npk,
    tokenHash,
    note.value,
  );
  if (expectedCommitment.toLowerCase() !== note.commitment.toLowerCase()) {
    fail("encrypted note commitment does not match its plaintext");
  }
}
